import { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import RGL, { WidthProvider } from "react-grid-layout";
import dayjs from "dayjs";
import "./dragAndDropStyle.css";
import AppointmentCard from "./AppointmentCard";

const ReactGridLayout = WidthProvider(RGL);

const DragDropAppointmentList = ({ navigate, blockTimeHeight, allowCreateBlockTime }) => {
    const appointments = useSelector((state) => state.appointment.appointments);
    const menu = useSelector((state) => state.generalInfo.menu);
    const staffInfo = useSelector((state) => state.generalInfo.staff);

    const [layout, setLayout] = useState([{ i: "0", x: 0, y: 0, w: 1, h: 1 }]);

    const onItemClick = (e, service) => {
        if (service.type === 3) {
            if (allowCreateBlockTime) {
                navigate(`/block-time?aid=${service.appointmentId}&date=${service.date}&start=${service.start}&end=${service.end}&notes=${service.notes}`);
            }
        } else {
            navigate(`/appointment?aid=${service.appointmentId}`);
        }
    };

    const countClientDubPhone = (value, checkAppointments) => {
        let countDup = 0;
        for (let appointment of checkAppointments) {
            if (appointment.phone === value) {
                countDup++;
            }
        }
        return countDup;
    };

    const isEarlyCheckedInAppointment = (appointment) => {
        if (!appointment.checkedInAt) {
            return false;
        }
        let appointmentTime = dayjs(`${appointment.date} ${appointment.services[0].start}`, 'YYYY-MM-DD hh:mm');
        let checkedInTime = dayjs(appointment.checkedInAt);
        if (appointment.type === 1 && appointment.status === "Checked-in" && appointment.checkedInByClient && appointmentTime.diff(checkedInTime, 'minute') > 15) {
            return true;
        }
        return false;
    };

    const generateColorMapForService = () => {
        const colors = {};
        if (!menu) return colors;

        menu.forEach((service) => {
            colors[service._id] = service.color;
        });
        return colors;
    };

    const checkLayoutItemOverlapped = (layoutItems) => {
        const overlapGroups = [];
        const indexToGroup = {};
        const overlapItems = new Set();

        for (let i = 0; i < layoutItems.length; i++) {
            for (let j = i + 1; j < layoutItems.length; j++) {
                const a = layoutItems[i];
                const b = layoutItems[j];

                const sameX = Math.floor(a.x) === Math.floor(b.x);
                const overlapY = a.y < b.y + b.h && a.y + a.h > b.y;

                if (sameX && overlapY) {
                    let groupA = indexToGroup[i];
                    let groupB = indexToGroup[j];

                    if (groupA !== undefined && groupB !== undefined) {
                        if (groupA !== groupB) {
                            const merged = [...overlapGroups[groupA], ...overlapGroups[groupB]];
                            overlapGroups[groupA] = merged;
                            overlapGroups[groupB] = [];
                            merged.forEach(idx => indexToGroup[idx] = groupA);
                        }
                    } else if (groupA !== undefined) {
                        overlapGroups[groupA].push(j);
                        indexToGroup[j] = groupA;
                    } else if (groupB !== undefined) {
                        overlapGroups[groupB].push(i);
                        indexToGroup[i] = groupB;
                    } else {
                        const groupIndex = overlapGroups.length;
                        overlapGroups.push([i, j]);
                        indexToGroup[i] = groupIndex;
                        indexToGroup[j] = groupIndex;
                    }

                    overlapItems.add(i);
                    overlapItems.add(j);
                }
            }
        }

        overlapGroups.forEach(group => {
            if (group.length === 0) return;
            const baseX = Math.floor(layoutItems[group[0]].x);
            const newWidth = 1 / group.length;
            group.forEach((idx, order) => {
                layoutItems[idx].w = newWidth;
                layoutItems[idx].x = baseX + newWidth * order;
            });
        });

        layoutItems.forEach((item, idx) => {
            if (!overlapItems.has(idx)) {
                item.w = 1;
                item.x = Math.floor(item.x);
            }
        });

        return layoutItems;
    };

    const displayServiceBackgroundColor = (service) => {
        if (service.status === "Completed") {
            return "#d9d9d9";
        }
        if (service.color) {
            return service.color;
        }
        return "#d9d9d9";
    };

    useEffect(() => {
        if (!appointments || appointments.length === 0 || !staffInfo) {
            return;
        }

        const colors = generateColorMapForService();

        let checkDupNameAppointments = [];
        const services = appointments.map((appointment) => {
            checkDupNameAppointments.push(appointment);
            const formattedService = appointment.services.map(
                (service, index) => {
                    const newService = { ...service };

                    for (const [key] of Object.entries(appointment)) {
                        if (!["_id", "services"].includes(key)) {
                            newService[key] = appointment[key];
                        }
                    }

                    newService.clientName =
                        appointment.clientName === "Unknown"
                            ? appointment.phone
                                ? "Unknown"
                                : appointment.clientName
                            : appointment.clientName;
                    newService.countDupPhone = countClientDubPhone(
                        appointment.phone,
                        checkDupNameAppointments
                    );
                    newService.countService = appointment.services.length;
                    newService.appointmentId = appointment._id;
                    newService.color = colors[service._id] || "#d9d9d9";
                    newService.index = index;
                    newService.type = appointment.type;
                    newService.isEarlyCheckedIn = isEarlyCheckedInAppointment(appointment);
                    newService.images = appointment.images?.length > 0 ? appointment.images : []

                    return newService;
                }
            );
            return formattedService;
        });

        let serviceList = services.flat().filter((service) => {
            return service.staff === staffInfo._id;
        });

        let newLayout = serviceList.map((service) => {
            return {
                x: 0,
                y:
                    dayjs(service.start, "hh:mm").diff(
                        dayjs("06:00", "hh:mm"),
                        "minutes"
                    ) / 15,
                w: 1,
                h: service.duration / 15,
                i: service.appointmentId + "." + service.index,
                service: service,
            };
        });

        newLayout = checkLayoutItemOverlapped(newLayout);
        setLayout(newLayout);
        // eslint-disable-next-line 
    }, [appointments, staffInfo, menu]);

    return (
        <div>
            <ReactGridLayout
                layout={appointments.length > 0 ? layout : []}
                style={{ marginLeft: 40, height: `${blockTimeHeight * 18 * 4}px` }}
                rowHeight={blockTimeHeight}
                cols={1}
                className='layout'
                compactType={null}
                preventCollision={true}
                margin={[2, 0]}
                isDraggable={false}
                isResizable={false}
            >
                {appointments.length > 0
                    ? layout.map((item) => {
                        const { service } = item;
                        if (!service) {
                            return null;
                        }

                        return (
                            <div
                                key={item.i}
                                style={{
                                    backgroundColor: displayServiceBackgroundColor(service),
                                    zIndex: 4,
                                    borderRadius: "5px",
                                    padding: "4px",
                                    cursor: "pointer",
                                    overflow: "hidden",
                                    borderBottom: "1px solid #e8e8e8",
                                }}
                                onClick={(e) => {
                                    onItemClick(e, service);
                                }}
                            >
                                <AppointmentCard service={service} />
                            </div>
                        );
                    })
                    : null}
            </ReactGridLayout>
        </div>
    );
};

export default DragDropAppointmentList;